import protobuf from 'protobufjs'

import {
    generateUuidv4,
    getCurrentDomain,
    getOsType,
    getPlatform,
    getPlatformEnum,
    onVisibilityChange,
} from './utils/helpers'
import { getQueryParams, mapQueryParams } from './utils/reEngagement'

const apiEndpoint = {
    production: 'https://brojke.superology.pro/kolektor/v2',
    staging: 'https://brojke.superology.dev/kolektor/v2',
    local: 'http://10.150.0.179:3000',
}

export default {
    /**
     * Superbet Analytics
     *
     * @param {Vue} Vue - Vue instance
     *
     * @param {Object}      options - Config params
     * @param {Boolean}     options.debug - Debug flag that enables some console logs
     * @param {Boolean}     options.testMode - Uses localhost IP for sending events
     * @param {Function}    options.user - Vuex user getter
     * @param {Function}    options.getLaunchDarklyAnalyticsFlag - Launch Darkly flag getter (promise)
     * @param {String}      options.deviceId - external device ID generated from shell
     * @param {Object}      options.appInfo - Language / build / version
     * @param {String}      options.appInfo.country - App country (RO/PL/HR/COM/RS/BE/BG)
     * @param {String}      options.appInfo.language - App language (RO/PL/HR)
     * @param {String}      options.appInfo.version - App version (ex v1.197.0)
     */
    // eslint-disable-next-line sonarjs/cognitive-complexity
    install(Vue, options) {
        // bind plugin to the main Vue instance
        Vue.prototype.$superAnalytics = {
            config: {
                // Launch Darkly
                getLaunchDarklyAnalyticsFlag: options.getLaunchDarklyAnalyticsFlag,

                // shows dev console logs
                debug: options.debug,

                // re-routes pings to localhost
                testMode: options.testMode || false,

                // currently set lower, so we don't wait for a bigger bulk on dev/stage
                maxBulkSize: options.testMode || options.debug ? 5 : 20,

                // localstorage key that holds the device ID
                deviceIdKey: '_sbaDeviceId',

                // sessionStorage key that holds the event bulk
                eventBulkKey: '_sbaQueue',

                // sessionStorage key that holds the session ID
                sessionIdKey: '_sbaSessionId',

                // sessionStorage key that holds deffered events (case when we open new tabs but don't want to fire all the events at that time since we want to track the session only with visible tabs)
                deferredEventsKey: '_sbaDeferredEvents',

                // unmount and visibility change might fire both on tab close, so we're tracking it with a session storage flag
                unloadedKey: '_sbaUnloaded',

                // Vuex reference - used for accessing the user object (might get expanded in the future)
                store: {
                    getUser: options.user,
                },

                // use our dev analytics endpoint for non-production environments
                // eslint-disable-next-line no-nested-ternary
                api: options.testMode
                    ? apiEndpoint.local
                    : options.debug
                    ? apiEndpoint.staging
                    : apiEndpoint.production,

                appInfo: {
                    country: options?.appInfo?.country || 'unknown',
                    language: options?.appInfo?.language || 'unknown',
                    version: options?.appInfo?.version || '',
                },

                // will hold the actual proto schema once it's been lazy-loaded
                protoSchema: null,
            },

            // gets updated by each page visit event and is sent in the next event as a part of the base event model
            lastScreenVisit: '',

            /**
             * Logs the array items into the console - just used for testing/debugging
             * @param {Array<any>} messages - mixed array of items to log
             */
            debugLog(messages) {
                if (this.config.debug) {
                    messages.forEach(msg => console.log(msg))
                }
            },

            /**
             * Sets up device/session ids
             *
             * since sessionStorage can persist on tab reload, we always store a new value
             * otherwise, we would be sending the same session id with session start on each reload
             */
            initAnalytics() {
                this.debugLog(['initializing SuperAnalytics'])

                this.getDeviceId()

                this.sessionStart()

                this.checkForNonOrganicVisit()
            },

            sessionStart() {
                if (!document.hidden) {
                    const dt = Intl.DateTimeFormat().resolvedOptions()

                    const sessionStartPayload = {
                        eventType: 'session_start',
                        sessionSessionStart: {
                            appBundleId: `${this.config.appInfo.country}.${getCurrentDomain()}.web`,
                            appVersion: this.config.appInfo.version,
                            appBuildNumber: '',
                            appPlatform: getPlatform(),
                            deviceManufacturer: '',
                            deviceModel: window.navigator.userAgent,
                            deviceOsType: getOsType(),
                            deviceOsVersion: '',
                            deviceTimezone: dt.timeZone,
                            deviceLanguage: this.config.appInfo.language,
                            deviceInstallationId: {
                                value: this.getDeviceId(),
                            },
                            deviceAdvertisingId: {
                                value: '',
                            },
                            appCountry: {
                                value: this.config.appInfo.country,
                            },
                        },
                        AnalyticsEvent: 'sessionSessionStart',
                    }

                    // remove flags of unloading/session end events
                    sessionStorage.removeItem(this.config.unloadedKey)

                    // create new session id
                    sessionStorage.setItem(this.config.sessionIdKey, generateUuidv4())

                    // push the event into the bulk
                    this.sendEvent(sessionStartPayload)

                    this.sendDeferredEvents()
                }
            },

            sessionEnd() {
                // send event only if it hasn't been done so by a previous event listener
                if (!sessionStorage.getItem(this.config.unloadedKey)) {
                    const sessionEndPayload = {
                        eventType: 'session_end',
                        sessionSessionEnd: {},
                        AnalyticsEvent: 'sessionSessionEnd',
                    }

                    sessionStorage.setItem(this.config.unloadedKey, 'true')

                    this.sendEvent(sessionEndPayload, true)
                }
            },

            checkForNonOrganicVisit() {
                const queryParams = getQueryParams()
                const mappedQueryParams = queryParams ? mapQueryParams(queryParams) : null

                if (mappedQueryParams) {
                    this.sendEvent({
                        eventType: 'session_attribution',
                        sessionAttribution: mappedQueryParams,
                        AnalyticsEvent: 'sessionAttribution',
                    })
                }
            },

            /**
             * Pushes the given event into the event bulk and sends it to the backend
             * if the stack has {bulkSize} events
             * @param {Object} event - Prepared event object
             * @param {Boolean} ignoreVisibility - Sends event regardless of screen visibility (important for session ends)
             */
            sendEvent(event, ignoreVisibility = false) {
                if (document.hidden && !ignoreVisibility) {
                    this.deferEvent(event)
                } else {
                    const newEvent = {
                        ...this.getBaseEventModel(),
                        ...event,
                    }

                    const eventBulk = this.getEvents()
                    eventBulk.push(newEvent)

                    this.setEvents(eventBulk)

                    if (event.eventType === 'session_screen_visit') {
                        this.lastScreenVisit = event.sessionSessionScreenVisit.screenName
                    }
                }
            },

            /**
             * Returns a list of prepared events
             * @returns {Array} - Prepared events
             */
            getDeferredEvents() {
                const previousDeferredEvents = sessionStorage.getItem(this.config.deferredEventsKey)

                if (previousDeferredEvents) {
                    try {
                        return JSON.parse(previousDeferredEvents)
                    } catch {
                        sessionStorage.removeItem(this.config.deferredEventsKey)

                        return []
                    }
                }

                return []
            },

            /**
             * Pushes the given event into the deferred bulk
             * Used when opening new tabs, so we don't double-trigger events before users actually switch to that tab
             * @param {Object} event - Prepared event object
             */
            deferEvent(event) {
                const deferredEvents = this.getDeferredEvents()
                deferredEvents.push(event)

                sessionStorage.setItem(this.config.deferredEventsKey, JSON.stringify(deferredEvents))
            },

            sendDeferredEvents() {
                const deferredEvents = this.getDeferredEvents()

                deferredEvents.forEach(event => this.sendEvent(event))

                sessionStorage.removeItem(this.config.deferredEventsKey)
            },

            /**
             * Checks for previous device IDs or creates a new one
             * @returns {String} - uuid string
             */
            getDeviceId() {
                if (options.deviceId) {
                    return options.deviceId
                }

                const existingDeviceId = localStorage.getItem(this.config.deviceIdKey)

                if (existingDeviceId) {
                    return existingDeviceId
                }

                const newDeviceId = generateUuidv4()
                localStorage.setItem(this.config.deviceIdKey, newDeviceId)

                return newDeviceId
            },

            /**
             * Returns the session id (created on init)
             * @returns {String} - uuid string
             */
            getSessionId() {
                return sessionStorage.getItem(this.config.sessionIdKey)
            },

            /**
             * Returns the event bulk if it's already present
             * @returns {Array<Object>} - Event bulk array
             */
            getEvents() {
                const previousEvents = sessionStorage.getItem(this.config.eventBulkKey)
                let parsedPreviousEvents = []

                // let's be a bit defensive - if someone tampers with the sessionStorage bulk, we'll just ignore it
                // better to lose a few old events than to break the script
                if (previousEvents) {
                    try {
                        parsedPreviousEvents = JSON.parse(previousEvents)
                    } catch {
                        this.setEvents([])
                    }
                }

                return parsedPreviousEvents
            },

            /**
             * Saves the event bulk to sessionStorage
             * @param {Array<Object>} events - Event bulk array
             */
            setEvents(events) {
                sessionStorage.setItem(this.config.eventBulkKey, JSON.stringify(events))

                const isUnloading = sessionStorage.getItem(this.config.unloadedKey)

                /*
                 * send events if we're capped and proto schema has been loaded
                 *
                 * in case the user leaves with {bulkCap} -1 events saved and the browser fails
                 * to fire the unmount event - the next sessionStart event would send the bulk
                 * to the backend. since we're lazy-loading our protoschema, there's a chance
                 * it won't be loaded in time.
                 *
                 * if this is the case, nothing happens - we simply send everything on the
                 * next incoming event
                 */
                if (events.length >= this.config.maxBulkSize && this.config.protoSchema && !isUnloading) {
                    this.sendEventsToClickhouse(events)
                }
            },

            /**
             * Sends the actual events to the analytics backend
             * @param {Array<Object>} events - Event bulk array
             * @param {Boolean} forceSend - Ignores LaunchDarkly flag (used to send events on tab/browser close)
             */
            async sendEventsToClickhouse(events, forceSend = false) {
                const ldFlagEnabled = await this.config.getLaunchDarklyAnalyticsFlag()

                if (ldFlagEnabled || forceSend) {
                    try {
                        this.debugLog([`${this.getSessionId()} | sending analytics (${events.length})`, events])

                        // eslint-disable-next-line compat/compat
                        navigator.sendBeacon(this.config.api, this.prepareProto(events))

                        this.setEvents([])
                    } catch (error) {
                        this.debugLog(['Bulk sending failed', error])
                    }
                } else {
                    this.setEvents([])

                    this.debugLog(['LD Analytics flag disabled'])
                }
            },

            /**
             * Used to force send events on tab/window close
             */
            forceSendEventsToClickhouse() {
                this.sendEventsToClickhouse(this.getEvents(), true)
            },

            /**
             * Runs through the list of events and creates a protomodel of the
             * corresponding analytic event
             * @param {Array<Object>} events - Event bulk array
             * @returns {Uint8Array} - Protobuf array
             */
            prepareProto(events) {
                const writer = new protobuf.Writer()
                const root = protobuf.Root.fromJSON(this.config.protoSchema)
                const protoModel = root.lookupType('AnalyticsBaseEvent')

                events.forEach(event => {
                    protoModel.encodeDelimited(event, writer)
                })

                return writer.finish()
            },

            /**
             * @returns {Boolean} - User GDPR acceptance
             * */
            isGdprAccepted() {
                const user = this.config.store.getUser()

                return Boolean(user?.acceptsGeneralPrivacy && user?.acceptsCustomPrivacy)
            },

            /**
             * Returns default values for all analytic models
             * @returns {Object} - Default values for all analytic models
             */
            getBaseEventModel() {
                const sessionId = this.getSessionId()
                const now = Date.now()

                return {
                    id: generateUuidv4(),
                    sessionId,
                    timestamp: {
                        seconds: Math.floor(now / 1000),
                        nanos: Math.floor((now % 1000) * 1000000),
                    },
                    appPlatform: getPlatformEnum(),
                    appVersion: -1, // web has no version, so we send -1 as defined by the data analyst team
                    appBundleId: `${this.config.appInfo.country}.${getCurrentDomain()}.web`,
                    deviceInstallationId: this.getDeviceId(),
                    gdprAccepted: this.isGdprAccepted(),
                    userId: this.config.store.getUser()
                        ? { value: this.config.store.getUser().userId.toString() }
                        : null,
                    lastRecordedScreenVisit: {
                        value: this.lastScreenVisit,
                    },
                }
            },
        }

        // init analytics & start the session
        const superAnalytics = Vue.prototype.$superAnalytics

        // lazy load proto schema
        import('./schema/analyticsSchema')
            .then(data => {
                superAnalytics.config.protoSchema = data.default
            })
            .catch(error => superAnalytics.debugLog(['Error fetching analytics schema', error]))

        superAnalytics.initAnalytics()

        // handle session on tab swap
        onVisibilityChange(visible => {
            if (visible) {
                superAnalytics.sessionStart()

                superAnalytics.checkForNonOrganicVisit()
            } else {
                superAnalytics.sessionEnd()
            }
        })

        // listen for tab / browser close
        window.addEventListener('beforeunload', () => {
            superAnalytics.sessionEnd()

            superAnalytics.forceSendEventsToClickhouse()
        })
    },
}
